import { useEffect, useState } from 'react'

import { getIsPointInContiguousUs } from 'utils/locationHelpers'

import { ICoordinates } from '../../types'

// default coordinates are set to Manhattan, NYC
const DEFAULT_COORDINATES: ICoordinates = { latitude: 40.787373, longitude: -73.987005 }

type IUseLocation = (
  ipLocation: {
    ipLatitude: number | undefined
    ipLongitude: number | undefined
  },
  mapBoundsCenter: ICoordinates | null,
  searchFoundLocationCenter: ICoordinates | null,
) => {
  location: ICoordinates | null
  onLocationChange: (location: ICoordinates) => void
}

/**
 * location (latitude and longitude) state
 *
 * we can get this from browser, ip, from user search, or from user moving map
 */
const useLocation: IUseLocation = (
  { ipLatitude, ipLongitude },
  mapBoundsCenter,
  searchFoundLocationCenter,
) => {
  const [location, setLocation] = useState<ICoordinates | null>(null)
  const onLocationChange = (newLocation: ICoordinates) => setLocation(newLocation)

  // when the page first loads, we try to initially set the location based on the user's
  // current location
  useEffect(() => {
    // we've already retrieved the location from IP, so we don't need to ask the browser for it
    // this is for Chrome specifically, where browser location requests are very slow
    if (ipLatitude && ipLongitude) return

    window.navigator.geolocation.getCurrentPosition(
      position => {
        console.warn({ position })
        const { latitude, longitude } = position.coords

        const isInContinentalUS = getIsPointInContiguousUs({ latitude, longitude })
        if (!isInContinentalUS) setLocation(DEFAULT_COORDINATES)
        else setLocation({ latitude, longitude })
      },
      error => {
        console.warn({ error })
        // we potentially want to use ipData service to try IP if this fails for some reason
        // unrelated to user permissions, e.g.
        // if (error.code !== error.PERMISSION_DENIED) {}
        // for now we just set it to a default location
        setLocation(DEFAULT_COORDINATES)
      },
      // We can experiment with these options in the future if we want to be more aggressive
      // {
      // enableHighAccuracy: true,
      // maximumAge: 86400,
      // },
    )
  }, [])

  useEffect(() => {
    if (ipLatitude && ipLongitude) {
      const isInContinentalUS = getIsPointInContiguousUs({
        latitude: ipLatitude,
        longitude: ipLongitude,
      })
      if (!isInContinentalUS) setLocation(DEFAULT_COORDINATES)
      else setLocation({ latitude: ipLatitude, longitude: ipLongitude })
    }
  }, [ipLatitude, ipLongitude])

  useEffect(() => {
    if (mapBoundsCenter?.latitude && mapBoundsCenter?.longitude) setLocation(mapBoundsCenter)
  }, [mapBoundsCenter])

  useEffect(() => {
    if (searchFoundLocationCenter?.latitude && searchFoundLocationCenter?.longitude) {
      setLocation(searchFoundLocationCenter)
    }
  }, [searchFoundLocationCenter])

  return {
    location,
    onLocationChange,
  }
}

export default useLocation
